import { Info } from '@mui/icons-material';
import {
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    type TooltipProps,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';

// The segments share the row equally.
const toggleGroupSx = {
    '& .MuiToggleButton-root': { flex: 1 },
};

export interface InlineToggleOption<T> {
    value: T;
    /** Message id. The label is one or two words; the tooltip carries the
     * explanation. */
    labelId: string;
}

interface InlineToggleProps<T> {
    labelId: string;
    tooltipId: string;
    value: T;
    /** Exactly two, because this is a switch and not a menu. */
    options: [InlineToggleOption<T>, InlineToggleOption<T>];
    onChange: (val: T) => void;
    disabled?: boolean;
    /** Which way it opens. Never toward the buttons it explains. */
    tooltipPlacement?: TooltipProps['placement'];
}

/**
 * A labelled two-way switch for a setting that sits inline with the
 * controls it governs, rather than in a form row of its own.
 */
export default function InlineToggle<T extends string | number | boolean>({
    labelId,
    tooltipId,
    value,
    options,
    onChange,
    disabled,
    tooltipPlacement = 'top',
}: InlineToggleProps<T>) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontSize={12}>
                    <FormattedMessage id={labelId} />
                </Typography>
                <Tooltip
                    title={<FormattedMessage id={tooltipId} />}
                    placement={tooltipPlacement}
                >
                    <Info sx={{ fontSize: 12, opacity: 0.6 }} />
                </Tooltip>
            </Box>
            <ToggleButtonGroup
                color="primary"
                value={value}
                exclusive
                onChange={(_, v) => v !== null && onChange(v as T)}
                size="small"
                disabled={disabled}
                sx={toggleGroupSx}
            >
                {options.map((option) => (
                    <ToggleButton
                        key={String(option.value)}
                        value={option.value}
                    >
                        <FormattedMessage id={option.labelId} />
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>
    );
}
